import { fetchReasonsUseQuery } from '@api/useQueryCalls';
import SkeletonComponent from '@atoms/SkeletonComponent';
import { Button } from '@components/primitives';
import ModalHeader from '@components/primitives/ModalHeader';
import ReasonsList from '@molecules/ReasonsList';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, View } from 'react-native';
import { logHideReasonsModal, logShowReasonsModal, logWithdrawalRequest } from './analyticsReasonsModal';
import styles from './styles';

/**
 *
 * @param {array} reasons
 * @returns JSX
 */

type ReasonData = {
  id: number;
};

type Account = {
  id: string;
};

type ReasonsForSelectionProps = {
  reasons: any[];
  [key: string]: any;
};

function ReasonsForSelection(props: ReasonsForSelectionProps): React.JSX.Element | null {
  const { reasons } = props;
  if (reasons && reasons.length > 0) {
    return <ReasonsList {...props} />;
  }
  return null;
}

type ReasonsModalProps = {
  source?: string;
  transaction_category_id?: string;
  withdrawalAmount?: number;
  showReasonSelector?: boolean;
  setShowReasonSelector?: (value: boolean) => void;
  withdrawRequest?: (args: {
    bankAccountNumber?: string;
    requestedAmount: number;
    reasonId: number;
    accountNumber: string;
    referralCode?: string;
    reasonDetail: string;
  }) => void;
  setReason?: (reason: ReasonData) => void;
  reasonData?: ReasonData;
  position?: 'INCREASE' | 'DECREASE' | string;
  account: Account;
  isEditReasons?: boolean;
};

const ReasonsModal = ({
  showReasonSelector = false,
  setShowReasonSelector = () => {},
  withdrawalAmount = 0,
  withdrawRequest = () => {},
  reasonData = { id: 0 },
  setReason = () => {},
  position = 'DECREASE',
  account,
  isEditReasons = false,
  source = '',
  transaction_category_id = '',
}: ReasonsModalProps): React.JSX.Element => {
  const { t } = useTranslation('common');
  const [reasonsDisplay, setReasonsDisplay] = useState<any[] | null>(null);
  const [disableContinue, setDisableContinue] = useState(true);
  const { validatedReferralCode, bankForTransfer, setCustomReasonText, selectedAccount, meAmplitudeContext } =
    getStore();
  const { amplitudeAccountName } = selectedAccount;
  const [customReasonText, setCustomReasonTextLocal] = useState('');
  // Remove ref - control with index prop

  const { data, isSuccess, refetch } = fetchReasonsUseQuery({
    accountNumber: account.id,
    reasonType: position === 'INCREASE' ? 'INCOME' : 'EXPENSE',
  });

  useEffect(() => {
    setReasonsDisplay(data);
  }, [isSuccess, data]);

  useEffect(() => {
    setCustomReasonText('');
    setReason(reasonData);
    refetch();
  }, []);

  useEffect(() => {
    if (showReasonSelector) {
      logShowReasonsModal(source, amplitudeAccountName, validatedReferralCode);
    }
  }, [showReasonSelector]);

  const closeReasonSelectorModal = () => {
    logHideReasonsModal(source, amplitudeAccountName, validatedReferralCode);
    setShowReasonSelector(false);
  };
  return (
    <Modal
      visible={showReasonSelector}
      animationType="none"
      presentationStyle="pageSheet"
      onRequestClose={closeReasonSelectorModal}>
      <View style={[styles.modalContainer, { backgroundColor: colors.neutral.cotton }]}>
        <ModalHeader
          title={t('withdrawlSlider.withdrawalReason')}
          onClose={() => {
            logHideReasonsModal(source, amplitudeAccountName, validatedReferralCode);
            setShowReasonSelector(false);
            setReason(reasonData);
          }}
        />

        <View style={styles.contentContainer}>
          {data && data.length > 0 ? (
            <ReasonsForSelection
              reasons={reasonsDisplay}
              setReasonsDisplay={setReasonsDisplay}
              setDisableContinue={setDisableContinue}
              setCustomReasonText={setCustomReasonTextLocal}
              isEditReasons={isEditReasons}
              source={source}
              transaction_category_id={transaction_category_id}
            />
          ) : (
            <SkeletonComponent />
          )}
        </View>

        <View style={[styles.footerContainer, { borderTopColor: colors.border.veryLightGray }]}>
          <Button
            size="lg"
            // flex={1}
            testID="btnCommonContinue"
            alignSelf="stretch"
            variant="solid"
            isDisabled={disableContinue}
            paddingY="10px"
            onPress={() => {
              if (withdrawRequest) {
                setCustomReasonText(customReasonText);
                logWithdrawalRequest(
                  amplitudeAccountName,
                  withdrawalAmount,
                  reasonData,
                  validatedReferralCode,
                  transaction_category_id,
                  source,
                  meAmplitudeContext,
                );
                withdrawRequest({
                  bankAccountNumber: bankForTransfer?.accountNumber,
                  requestedAmount: Math.floor(withdrawalAmount),
                  reasonId: reasonData.id,
                  accountNumber: account.id,
                  referralCode: validatedReferralCode,
                  reasonDetail: customReasonText,
                });
              }
              setShowReasonSelector(false);
            }}
            _text={{
              fontWeight: 'bold',
            }}>
            {t('common.continue')}
          </Button>
        </View>
      </View>
    </Modal>
  );
};

export default ReasonsModal;
